/**
 * RequestsByTypeWidget — pixel-matched to the Penpot "Requests By Type"
 * mockup. Horizontal stacked bar per request type, normalized so the
 * biggest row fills the bar track and shorter rows are proportionally
 * shorter. Bottom legend names every status color.
 *
 * Data:
 *   - request_parent.link → row label (dynamic per business; the user
 *     confirmed we use the actual DB values, not the Penpot canonical
 *     taxonomy of Service Plan / Repair / Install / Maintenance /
 *     Inspection)
 *   - request_parent.status → segment, lowercase varchar
 *     (new / in_progress / completed / pending / canceled)
 *
 * The Penpot legend shows 4 buckets (Open / In Progress / Closed / Lost).
 * We map our 5 real statuses into those 4 visual buckets so the chart
 * stays readable: pending → Open (waiting), canceled → Lost.
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import {
  ChartBarIcon,
  ChartLegend,
  RefreshButton,
  StackedBar,
  WidgetCard,
} from "components/dashboard";
import type { ChartLegendItem, StackedBarRow } from "components/dashboard";
import { useRequestsByType, formatQueryError } from "../hooks";
import type { LayoutItem, WidgetConfig } from "../types";
import {
  bucketRequestStatus,
  titleCase,
  type RequestLegendKey,
} from "./requestStatusBuckets";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

type LegendKey = RequestLegendKey;

// Penpot palette — exact #s from the mockup.
const LEGEND_COLORS: Record<LegendKey, string> = {
  open: "59,130,246",      // #3b82f6 blue-500
  in_progress: "245,158,11", // #f59e0b amber-500
  closed: "34,197,94",     // #22c55e green-500
  lost: "239,68,68",       // #ef4444 red-500
};

const LEGEND_LABELS: Record<LegendKey, string> = {
  open: "Open",
  in_progress: "In Progress",
  closed: "Closed",
  lost: "Lost",
};


export function RequestsByTypeWidget({ widget, layout }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useRequestsByType();

  // Pivot DB rows → StackedBar rows, collapsing 5 DB statuses into 4
  // legend buckets. Segments render in legend order (open / in_progress /
  // closed / lost) so colors line up visually with the legend below.
  const rows: StackedBarRow[] = (data?.types ?? []).map((typeRow) => {
    const byBucket: Record<LegendKey, number> = {
      open: 0,
      in_progress: 0,
      closed: 0,
      lost: 0,
    };
    for (const b of typeRow.buckets) {
      byBucket[bucketRequestStatus(b.status)] += b.count;
    }
    const orderedKeys: LegendKey[] = ["open", "in_progress", "closed", "lost"];
    return {
      key: typeRow.type,
      label: titleCase(typeRow.type),
      segments: orderedKeys
        .filter((k) => byBucket[k] > 0)
        .map((k) => ({
          key: `${typeRow.type}-${k}`,
          label: LEGEND_LABELS[k],
          value: byBucket[k],
          color: LEGEND_COLORS[k],
        })),
    };
  });

  const legendItems: ChartLegendItem[] = (["open", "in_progress", "closed", "lost"] as const).map(
    (k) => ({ key: k, label: LEGEND_LABELS[k], color: LEGEND_COLORS[k] }),
  );

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      icon={<ChartBarIcon />}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton
          onClick={() => queryClient.invalidateQueries(["dashboard", "requests-by-type"])}
        />
      }
      footer={<ChartLegend items={legendItems} />}
    >
      {/* Per-row 100% normalization — each row's bar fills the full track,
          segments size by percentage of the row's total. With real data the
          counts vary wildly (e.g. 2000+ for one type, 5 for another), so
          normalizing across rows hides smaller types' status breakdowns. */}
      <StackedBar rows={rows} labelWidth={84} countWidth={28} barHeight={8} />
    </WidgetCard>
  );
}
